import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { MapPin, Trophy, Send, Bell, CheckCircle, Building, ChevronDown, ChevronUp, Briefcase } from 'lucide-react';

const Matches = () => {
  const { user } = useAuth();
  const [matchData, setMatchData] = useState({ matches: [], qualified_roles: [] });
  const [expandedMatch, setExpandedMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionStates, setActionStates] = useState({});

  useEffect(() => {
    fetchMatches();
  }, [user.id]);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API_BASE}/employer-matches/${user.id}`);
      setMatchData(response.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendCV = async (match) => {
    setActionStates(prev => ({ ...prev, [`cv-${match.id}`]: 'loading' }));
    try {
      await axios.post(`${API_BASE}/employer-matches/send-cv`, {
        user_id: user.id,
        employer_id: match.employer_id,
        role_id: match.role_id,
      });
      toast.success(`CV sent to ${match.employer_name}!`);
      setActionStates(prev => ({ ...prev, [`cv-${match.id}`]: 'done' }));
    } catch (error) {
      toast.error('Failed to send CV');
      setActionStates(prev => ({ ...prev, [`cv-${match.id}`]: null }));
    }
  };

  const notifyEmployer = async (match) => {
    setActionStates(prev => ({ ...prev, [`notify-${match.id}`]: 'loading' }));
    try {
      await axios.post(`${API_BASE}/employer-matches/notify`, {
        user_id: user.id,
        employer_id: match.employer_id,
        role_id: match.role_id,
        score: match.assessment_score,
      });
      toast.success(`${match.employer_name} notified of your ${match.assessment_score}% score!`);
      setActionStates(prev => ({ ...prev, [`notify-${match.id}`]: 'done' }));
    } catch (error) {
      toast.error('Failed to notify employer');
      setActionStates(prev => ({ ...prev, [`notify-${match.id}`]: null }));
    }
  };

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]" />
      </div>
    );
  }

  const { matches, qualified_roles } = matchData;

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1d1d1f] mb-3">
            Employer Matches
          </h1>
          <p className="text-lg text-[#86868b]">
            {matches.length > 0
              ? `${matches.length} employer match${matches.length > 1 ? 'es' : ''} based on your qualified roles`
              : 'Complete role-specific assessments with 80%+ to see employer matches'}
          </p>
        </div>

        {/* Qualified Roles Summary */}
        {qualified_roles.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 mb-8">
            <h2 className="text-sm font-semibold text-[#86868b] uppercase tracking-wide mb-3">Your Qualified Roles</h2>
            <div className="flex flex-wrap gap-3">
              {qualified_roles.map((qr) => (
                <div
                  key={qr.role_id}
                  className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-medium"
                  data-testid={`qualified-role-${qr.role_id}`}
                >
                  <Trophy className="w-4 h-4" />
                  {qr.title}
                  <span className="bg-green-200 text-green-800 px-2 py-0.5 rounded-full text-xs font-bold">{qr.score}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No matches state */}
        {matches.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-12 text-center">
            <Briefcase className="w-12 h-12 text-[#86868b] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#1d1d1f] mb-2">No employer matches yet</h3>
            <p className="text-[#86868b] mb-2">Complete your role-specific assessments with a score of 80% or higher to get matched with employers.</p>
            <p className="text-sm text-[#86868b]">Go to <span className="text-[#0071e3] font-medium">Saved Roles</span> to take your assessments.</p>
          </div>
        )}

        {/* Match Cards */}
        {matches.length > 0 && (
          <div className="space-y-4">
            {matches.map((match) => {
              const isExpanded = expandedMatch === match.id;
              const cvState = actionStates[`cv-${match.id}`];
              const notifyState = actionStates[`notify-${match.id}`];

              return (
                <div
                  key={match.id}
                  className="bg-white rounded-2xl border border-[#d2d2d7] overflow-hidden transition-all hover:shadow-md"
                  data-testid={`match-${match.id}`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {match.match_type === 'live_role' ? (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 uppercase tracking-wide">Live Role</span>
                          ) : (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 uppercase tracking-wide">Open to Applications</span>
                          )}
                          {match.is_local && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 uppercase tracking-wide">In Your Area</span>
                          )}
                          <span className="text-xs text-[#86868b]">{match.career_title}</span>
                        </div>

                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-xl sm:text-2xl font-semibold text-[#1d1d1f]">{match.employer_name}</h3>
                          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                            {match.assessment_score}% Qualified
                          </span>
                        </div>

                        <p className="text-base text-[#1d1d1f] mb-1">{match.role_title}</p>
                        <div className="flex items-center text-sm text-[#86868b]">
                          <MapPin className="w-4 h-4 mr-1" />
                          {match.role_location}
                        </div>
                      </div>

                      <button
                        onClick={() => setExpandedMatch(isExpanded ? null : match.id)}
                        className="p-2 rounded-xl hover:bg-[#f5f5f7] transition-colors"
                        data-testid={`expand-match-${match.id}`}
                      >
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-[#86868b]" /> : <ChevronDown className="w-5 h-5 text-[#86868b]" />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded section */}
                  {isExpanded && (
                    <div className="px-6 pb-6 pt-0 border-t border-[#f5f5f7]">
                      <div className="pt-4 mb-5">
                        <h4 className="text-sm font-semibold text-[#1d1d1f] mb-2">Why you're matched</h4>
                        <p className="text-sm text-[#86868b] leading-relaxed">
                          You scored <span className="font-semibold text-[#1d1d1f]">{match.assessment_score}%</span> on your {match.career_title} assessments, 
                          exceeding the 80% qualification threshold. 
                          {match.match_type === 'live_role'
                            ? ` ${match.employer_name} is actively hiring for this role in ${match.role_location}.`
                            : ` ${match.employer_name} accepts qualified candidates with scores above 80%.`}
                          {match.is_local && ' This employer is in your local area.'}
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button
                          onClick={() => sendCV(match)}
                          disabled={cvState === 'loading' || cvState === 'done'}
                          className={`rounded-full px-6 ${
                            cvState === 'done'
                              ? 'bg-green-600 text-white'
                              : 'bg-[#0071e3] hover:bg-[#0077ed] text-white'
                          }`}
                          data-testid={`send-cv-${match.id}`}
                        >
                          {cvState === 'done' ? (
                            <><CheckCircle className="w-4 h-4 mr-2" /> CV Sent</>
                          ) : cvState === 'loading' ? (
                            'Sending...'
                          ) : (
                            <><Send className="w-4 h-4 mr-2" /> Send CV</>
                          )}
                        </Button>

                        <Button
                          onClick={() => notifyEmployer(match)}
                          disabled={notifyState === 'loading' || notifyState === 'done'}
                          variant="outline"
                          className={`rounded-full px-6 ${
                            notifyState === 'done'
                              ? 'border-green-300 text-green-600'
                              : 'border-[#d2d2d7] text-[#1d1d1f] hover:bg-[#f5f5f7]'
                          }`}
                          data-testid={`notify-employer-${match.id}`}
                        >
                          {notifyState === 'done' ? (
                            <><CheckCircle className="w-4 h-4 mr-2" /> Employer Notified</>
                          ) : notifyState === 'loading' ? (
                            'Notifying...'
                          ) : (
                            <><Bell className="w-4 h-4 mr-2" /> Notify Employer of Score</>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Matches;
